import moment from "moment";

import * as i18n from "util/i18n";

import { FORM_ID } from "constants/servicePayments";

export function flatPaymentToArcaTaxServiceRow(payment, currencyLabel = "ARS") {
    const pending = !!payment?.isPending;
    const handleBaseDebts = payment?.entity?.handleBaseDebts === true;
    const concept = payment?.concept || {};
    const managesIncomeAmounts = concept.managesIncomeAmounts === true;
    const managesIncomeReference = concept.managesIncomeReference === true;
    const maxLengthTextReference = Number(concept.maxLengthTextReference ?? 0);

    const qtyRaw = payment?.amount?.quantity ?? concept?.amount ?? 0;
    const qty = Number(qtyRaw);
    const safeQty = Number.isFinite(qty) ? qty : 0;

    const currency = currencyLabel;

    const expirationAmount = Number(concept?.amount ?? qtyRaw);
    const snapQty = Number.isFinite(expirationAmount) ? expirationAmount : safeQty;

    let amountProps;
    if (!managesIncomeAmounts) {
        amountProps = handleBaseDebts
            ? {
                  variant: "formatted",
                  amount: snapQty,
                  currency,
              }
            : { variant: "dash" };
    } else {
        const amountSuffix = handleBaseDebts ? String(concept.debtId ?? "") : String(concept.conceptCode ?? "");
        const inputId = `${payment.key}_conceptAmount${amountSuffix}`;
        const baseEditable = {
            inputId,
            value: String(safeQty),
            onChange: () => {},
            currency,
            decimalScale: 2,
            fixedDecimalScale: true,
        };

        if (handleBaseDebts) {
            amountProps = {
                variant: "editableWhenSelected",
                snapshotWhenUnchecked: {
                    amount: snapQty,
                    currency,
                },
                editableWhenChecked: baseEditable,
            };
        } else {
            amountProps = {
                variant: "editableWhenSelected",
                snapshotWhenUnchecked: { variant: "dash" },
                editableWhenChecked: baseEditable,
            };
        }
        if (concept.managesRange === true) {
            const minAmt = Number(concept.minAmount);
            const maxAmt = Number(concept.maxAmount);
            if (Number.isFinite(minAmt) && Number.isFinite(maxAmt)) {
                amountProps = {
                    ...amountProps,
                    managesRange: true,
                    minAmount: minAmt,
                    maxAmount: maxAmt,
                };
            }
        }
    }

    let paymentReferenceProps;
    if (!managesIncomeReference) {
        paymentReferenceProps = { variant: "unavailable" };
    } else {
        paymentReferenceProps = {
            variant: "input",
            inputId: `${payment.key}_paymentReference`,
            value: String(payment.paymentReference ?? ""),
            onChange: () => {},
            maxLength: maxLengthTextReference,
        };
    }

    let expirationProps;
    if (handleBaseDebts && concept.date) {
        expirationProps = {
            hasExpirationDate: true,
            date: concept.date,
        };
    } else {
        expirationProps = {
            hasExpirationDate: false,
        };
    }

    return {
        selection: {
            id: payment.key,
            name: "payment-services-arca-tax",
            isChecked: false,
            onToggle: () => {},
            isDisabled: pending,
        },
        entity: {
            title: payment?.entity?.description ? String(payment.entity.description).toUpperCase() : "",
            subtitle: pending ? undefined : payment?.reference?.toUpperCase?.(),
            isPending: pending,
            href: payment?.link ?? undefined,
        },
        expiration: expirationProps,
        paymentReference: paymentReferenceProps,
        amount: amountProps,
        sourceFlatPayment: payment,
    };
}

function parseArcaAmountFromUiOrFormatted(raw) {
    if (typeof raw === "number" && Number.isFinite(raw)) {
        return raw;
    }
    const str = String(raw ?? "")
        .replace(/\s/g, "")
        .trim();
    if (!str) {
        return NaN;
    }
    const normalized = str.includes(",") ? str.replaceAll(".", "").replace(",", ".") : str;
    const n = Number(normalized);
    return Number.isFinite(n) ? n : NaN;
}

export function extractArcaFlatPaymentPatchFromServiceRow(serviceRow) {
    const amt = serviceRow?.amount;
    let quantity;
    if (!amt) {
        quantity = undefined;
    } else if (amt.variant === "formatted") {
        const n = parseArcaAmountFromUiOrFormatted(amt.amount);
        quantity = Number.isFinite(n) ? n : undefined;
    } else if (amt.variant === "dash") {
        quantity = undefined;
    } else if (amt.variant === "editableWhenSelected") {
        const n = parseArcaAmountFromUiOrFormatted(amt.editableWhenChecked?.value ?? "0");
        quantity = Number.isFinite(n) ? n : undefined;
    } else {
        quantity = undefined;
    }

    let paymentReference;
    const pr = serviceRow?.paymentReference;
    if (pr && pr.variant === "input") {
        paymentReference = String(pr.value ?? "");
    } else {
        paymentReference = undefined;
    }

    return { quantity, paymentReference };
}

export function mapArcaPaidVepFiltersToFetchParams(values, opts = {}) {
    const { isDesktop } = opts;
    const paidSub = values?.paidVepSubType?.value ?? values?.paidVepSubType;
    const subgroup = paidSub === "thirdparty" || paidSub === "taxpayer" || paidSub === "own" ? paidSub : "own";
    const generatedMap = { own: "1", thirdparty: "2", taxpayer: "3" };
    const generatedVEP = generatedMap[subgroup];

    let taxpayerCUIT = "";
    if (subgroup === "taxpayer") {
        const raw = values?.taxpayer;
        taxpayerCUIT = typeof raw === "object" && raw !== null ? raw?.value ?? "" : raw ?? "";
    }

    let numberVEP = "";
    if (subgroup === "taxpayer") {
        numberVEP = typeof values.taxpayerVepNumber === "string" ? values.taxpayerVepNumber : "";
    }

    const thirdPartyCuit =
        subgroup === "thirdparty" && typeof values.document === "string" ? values.document : undefined;

    const dateBlock = values?.paymentDate ?? values?.creationDates ?? values?.paymentDates;
    let dateFrom = null;
    let dateTo = null;
    const pickFrom =
        typeof dateBlock === "object" && dateBlock !== null
            ? dateBlock.dateFrom ?? dateBlock.from ?? dateBlock[0]
            : null;
    const pickTo =
        typeof dateBlock === "object" && dateBlock !== null ? dateBlock.dateTo ?? dateBlock.to ?? dateBlock[1] : null;

    dateFrom = pickFrom || null;
    dateTo = pickTo || null;

    if (dateFrom && !dateTo) {
        dateTo = moment();
    }
    if (dateTo && !dateFrom) {
        dateFrom = dateTo;
    }

    const dateFromFinal = dateFrom;
    const dateToFinal = dateTo;

    return {
        paymentType: values?.selectedVepType === "paid" ? "paymentsMade" : "stillToPay",
        generatedVEP,
        numberVEP,
        taxpayerCUIT,
        dateFrom: dateFromFinal,
        dateTo: dateToFinal,
        pageNumber: 1,
        thirdPartyCuit,
        isDesktop,
    };
}

export function mapPaymentsAFIPToVepPaidCardData(paymentsAFIP = []) {
    return paymentsAFIP.map((row) => ({
        id: String(row.numberVEP ?? ""),
        expiration: row.dueDate ?? "",
        vepNumber: row.numberVEP ?? "",
        description: row.description ?? "",
        concept: row.conceptDesc ?? "",
        subConcept: row.subConceptDesc ?? "",
        taxpayer: row.taxpayerCUIT ?? row.cuitContributor ?? "",
        contributorsCUIT: row?.contributorsCUIT,
        isPending: row.isPending,
        fiscalPeriod: (() => {
            if (typeof row.taxPeriodDesc === "string") {
                return row.taxPeriodDesc;
            }
            if (typeof row.period === "string") {
                return row.period;
            }
            return row?.fiscalPeriod;
        })(),
        userCUIT: row?.userCUIT,
        advanceQuota: row.advancePayment ?? "",
        amount: typeof row.amount === "object" && row.amount !== null ? row.amount.quantity ?? 0 : row.amount ?? 0,
        filterType: row?.filterType,
        formNumber: row?.formNumber,
    }));
}

export function getPaymentsArcaServiceCardLabel(key) {
    let raw;
    switch (key) {
        case "expiration":
            raw = i18n.get(`${FORM_ID}.agenda.expiration.label`);
            break;
        case "expirationNone":
            raw = i18n.get("services.newPayment.noExpirationDate");
            break;
        case "paymentReference":
            raw = i18n.get("services.newPayment.paymentRef.label");
            break;
        case "paymentReferenceUnavailable":
            raw = i18n.get("transfer.notAvailable");
            break;
        case "amount":
            raw = i18n.get("services.newPayment.amountToPay");
            break;
        case "statusPending":
            raw = i18n.get("services.payments.detail.status.PENDING");
            break;
        case "amountRangeMinCaption":
            raw = i18n.get("accounts.movements.filters.minAmount.label");
            break;
        case "amountRangeMaxCaption":
            raw = i18n.get("accounts.movements.filters.maxAmount.label");
            break;
        case "amountRangeError":
            raw = i18n.get("services.payments.field.concept.mustBeBetweenMinAndMax.error");
            break;
        default:
            raw = typeof key === "string" ? key : String(key);
    }
    return raw;
}
